import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api, ApiError, type Me } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

interface Row {
  id: number; name: string; everyMonths: number; nextDate: string; endDate: string | null; send: boolean; active: boolean;
  lastInvoiceId: number | null; lastRunAt: string | null; lastError: string | null; issued: number; buyerName: string; total: number | null;
}

export const EVERY: Record<number, string> = { 1: 'Kas mėnesį', 2: 'Kas 2 mėnesius', 3: 'Kas ketvirtį', 6: 'Kas pusmetį', 12: 'Kas metus' };

/** Periodinės sąskaitos: invoices issued again every period, as drafts or sent to the customer. */
export function RecurringPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['recurring'], queryFn: () => api.get<{ schedules: Row[] }>('api/v1/recurring').then((r) => r.schedules) });
  const remove = useMutation({ mutationFn: (id: number) => api.delete(`api/v1/recurring/${id}`), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recurring'] }) });
  const rows = list.data ?? [];
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Pajamos')} title={t('Periodinės sąskaitos')} text={t('Sąskaitos, kurios išrašomos automatiškai kas mėnesį, ketvirtį ar metus.')}
      action={<Link className="button button--primary" to="/recurring/new">+ {t('Nauja periodinė sąskaita')}</Link>} />}>
      {remove.isError ? <Alert kind="error">{errorText(t, remove.error)}</Alert> : null}
      <section className="panel-card">
        <div className="table-wrap"><table className="invoice-table">
          <thead><tr><th>{t('Pavadinimas')}</th><th>{t('Kartoti')}</th><th>{t('Kita sąskaita')}</th><th>{t('Išrašyta')}</th><th>{t('Būsena')}</th><th className="right">{t('Suma')}</th><th /></tr></thead>
          <tbody>
            {list.isSuccess && !rows.length ? <tr><td colSpan={7} className="empty">{t('Periodinių sąskaitų dar nėra. Sukurkite naują arba atidarykite išrašytą sąskaitą ir spauskite „Kartoti periodiškai“.')}</td></tr> : null}
            {rows.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.name}</strong><small>{r.buyerName}</small></td>
                <td>{t(EVERY[r.everyMonths] ?? '')}{r.endDate ? <small>{t('iki {date}', { date: r.endDate })}</small> : null}</td>
                <td>{r.active ? r.nextDate : '—'}<small>{r.send ? t('siunčiama el. paštu') : t('juodraštis')}</small></td>
                <td>{r.issued}{r.lastInvoiceId ? <small><Link to="/outgoing/$id" params={{ id: String(r.lastInvoiceId) }}>{t('paskutinė')}</Link></small> : null}</td>
                <td>{r.lastError ? <span className="badge badge--overdue" title={errorText(t, new ApiError(400, r.lastError))}>{t('Klaida')}</span>
                  : <span className={`badge ${r.active ? 'badge--sent' : 'badge--cancelled'}`}>{r.active ? t('Aktyvi') : t('Sustabdyta')}</span>}
                  {r.lastError ? <small className="text-late">{errorText(t, new ApiError(400, r.lastError))}</small> : null}</td>
                <td className="right"><strong>{r.total === null ? '—' : money(r.total)}</strong></td>
                <td className="row-actions">
                  <Link to="/recurring/$id" params={{ id: String(r.id) }}>{t('Redaguoti')}</Link>
                  <button type="button" className="delete-x" aria-label={t('Pašalinti periodinę sąskaitą? Jau išrašytos sąskaitos liks.')}
                    onClick={() => { if (confirm(t('Pašalinti periodinę sąskaitą? Jau išrašytos sąskaitos liks.'))) remove.mutate(r.id); }}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </section>
    </PanelShell>
  );
}
